import React from "react";
import CustomContainer from "../Components/CustomContainer";
import Loader from "../Components/Loader";
import ConstructionSiteWrapper from "../Components/ConstructionSiteWrapper";
import { generatePath, useParams } from "react-router-dom";
import ConstructionSiteAction from "../../Actions/ConstructionSiteAction";
import Project from "../Home/Components/Project";
import { Breadcrumb, Button } from "react-bootstrap";
import CustomTranslation from "../Components/CustomTranslation";
import ICLItem from "./Components/ICLItem";
import Uuid from "uuid/v1";
import { toast } from "react-hot-toast";
import ICLAction from "../../Actions/ICLAction";
import ICLParent from "./Components/ICLParent";
import UserAction from "../../Actions/UserAction";
import CustomInput from "../Components/CustomInput";
import CustomCheckBox from "../Components/CustomCheckBox";
import TranslationAction from "../../Actions/TranslationAction";
import TranslationButton from "../Components/TranslationButton";
import TranslationHint from "../Components/TranslationHint";

class ICLScreen extends React.Component {

    //constructor
    constructor(props) {
        super(props);

        //set state
        this.state = {
            title: '',
            ...TranslationAction.getInitialTranslationState()
        };

        this.titleTimeout = null;
        this.rawItems = [];
    }

    componentDidMount = () => {
        this.updateElements();
        this.checkTranslationHint();
    }

    //show the translation hint balloon if the user hasn't seen it before
    checkTranslationHint = () => {
        const seen = TranslationAction.hasSeenTranslationHint();
        if (!seen) {
            this.setState({ showTranslationHint: true });
        }
    }

    //dismiss the translation hint balloon and remember that it was seen
    dismissTranslationHint = () => {
        this.setState({ showTranslationHint: false });
        TranslationAction.markTranslationHintSeen();
    }

    updateElements = async () => {

        //get construction site
        let cs = await ConstructionSiteAction.getConstructionSite(this.props.params.construction_site_id);

        //init icl
        let ICL = {};

        //create icl if needed
        if (this.state.ICL) {
            ICL = await ICLAction.getICL(this.state.ICL.id);
        } else if (this.props.params.icl_id) {
            ICL = await ICLAction.getICL(this.props.params.icl_id);
        } else {
            ICL = await ICLAction.createICL(cs.id);
        }

        //get icl items
        let items = await ICLAction.getItems(ICL.id);
        this.rawItems = items;

        //title
        let title = '';
        if (ICL.title && typeof ICL.title === 'object' && Object.keys(ICL.title).length > 0) {
            title = await TranslationAction.translateArray(ICL.title);
        }

        //get current user (needed for translation preferences)
        let currentUser = await UserAction.getCurrentUser();

        //pick up the active translation for this ICL, if any (e.g. set from the item screen -
        //navigation here is a hard page reload, so this is the only thing that survives it)
        const session = TranslationAction.getTranslationSession(ICL.id);

        //load available DeepL languages lazily, same as Assignment translation
        const languageState = await TranslationAction.loadAvailableLanguages(
            currentUser, this.state.availableLanguages, session?.selectedLanguage ?? this.state.selectedLanguage
        ) ?? {};

        const showTranslation = session ? session.showTranslation : this.state.showTranslation;
        const selectedLanguage = session?.selectedLanguage ?? languageState.selectedLanguage ?? this.state.selectedLanguage;

        //set state
        this.setState({
            data: {
                constructionSite: cs
            },
            ICL: ICL,
            title: title,
            currentUser: currentUser,
            ...languageState,
            showTranslation,
            selectedLanguage,
        }, () => {
            this.refreshICLObjects();
        });
    }

    //rebuild the rendered question list, translating it if a translation is active
    refreshICLObjects = async () => {
        const items = this.rawItems || [];

        if (this.state.showTranslation && this.state.selectedLanguage) {
            this.setState({ loadingTranslations: true });
            const override = await this.translateICLQuestions(items, this.state.selectedLanguage.value);
            this.setState({
                ICLObjects: this.buildICLObjects(items, override),
                loadingTranslations: false,
            });
        } else {
            this.setState({ ICLObjects: this.buildICLObjects(items, null) });
        }
    }

    //translate all question + category titles for the given items in one batched DeepL call
    translateICLQuestions = async (items, targetLangId) => {
        //resolve each item's own text (in the current UI language) and dedupe parent/category titles
        const parentTextsById = {};
        const itemTexts = [];

        for (const element of items) {
            let parent = null;
            let text = '';

            if (element.inspectionChecklistOption) {
                parent = element.inspectionChecklistOption.parent;
                text = await TranslationAction.translateArray(element.inspectionChecklistOption.title);
            } else if (element.inspectionChecklistOptionInsert) {
                parent = element.inspectionChecklistOptionInsert.inspectionChecklistOption;
                text = element.inspectionChecklistOptionInsert.title;
            }

            itemTexts.push(text);

            if (parent && !(parent.id in parentTextsById)) {
                parentTextsById[parent.id] = await TranslationAction.translateArray(parent.title);
            }
        }

        const parentIds = Object.keys(parentTextsById);
        const allTexts = [...parentIds.map(id => parentTextsById[id]), ...itemTexts];

        if (allTexts.length === 0) {
            return { itemTitles: [], parentTitles: {} };
        }

        const translations = await TranslationAction.translateTextList(allTexts, targetLangId);

        const parentTitles = {};
        parentIds.forEach((id, index) => { parentTitles[id] = translations[index]; });

        const itemTitles = itemTexts.map((text, index) => translations[parentIds.length + index]);

        return { itemTitles, parentTitles };
    }

    //build the ICLParent/ICLItem tree, using either the original (possibly untranslated) titles or an override
    buildICLObjects = (items, override = null) => {
        let ICLItemsMap = [];
        let ICLParents = [];
        let ICL = this.state.ICL;
        let cs = this.state.data.constructionSite;

        items.forEach((element, index) => {
            let parent = null;
            let titleNode = null;

            if (element.inspectionChecklistOption) {
                parent = element.inspectionChecklistOption.parent;
                titleNode = override ? override.itemTitles[index] : <CustomTranslation value={element.inspectionChecklistOption.title} />;
            } else if (element.inspectionChecklistOptionInsert) {
                parent = element.inspectionChecklistOptionInsert.inspectionChecklistOption;
                titleNode = override ? override.itemTitles[index] : element.inspectionChecklistOptionInsert.title;
            } else {
                return;
            }

            //check if array already exists
            if (typeof ICLItemsMap[parent.id] === 'undefined') {
                ICLItemsMap[parent.id] = [];
                ICLParents.push(parent);
            }

            //if element is option
            if (element.inspectionChecklistOption) {
                ICLItemsMap[parent.id].push(
                    <ICLItem
                        key={element.id + Uuid()}
                        title={titleNode}
                        value={element.status}
                        data={element}
                        className={'vertical-margin-3'}
                        onValueChange={(value) => {
                            element.status = value;
                            ICLAction.updateItem(element).then((result) => {
                                if (value === false) {
                                    window.location.href = generatePath('/construction_site/:construction_site_id/inspection_checklist/:icl_id/item/:item_id/:positive', { construction_site_id: cs.id, icl_id: ICL.id, item_id: result.id, positive: '0' });
                                }
                            });
                        }}
                        onAssignment={() => {
                            window.location.href = generatePath('/construction_site/:construction_site_id/inspection_checklist/:icl_id/item/:item_id/:positive', { construction_site_id: cs.id, icl_id: ICL.id, item_id: element.id, positive: '1' });
                        }}
                    />
                );
            } else {
                ICLItemsMap[parent.id].push(
                    <ICLItem
                        key={element.id + Uuid()}
                        title={titleNode}
                        value={element.status}
                        data={element}
                        className={'vertical-margin-3'}
                        onValueChange={(value) => {
                            element.status = value;
                            ICLAction.updateItem(element).then((result) => {
                                if (value === false) {
                                    window.location.href = generatePath('/construction_site/:construction_site_id/inspection_checklist/:icl_id/item/:item_id/:positive', { construction_site_id: cs.id, icl_id: ICL.id, item_id: result.id, positive: '0' });
                                }
                            });
                        }}
                        onAssignment={() => {
                            ICLAction.getItem(element.id).then((result) => {
                                window.location.href = generatePath('/construction_site/:construction_site_id/inspection_checklist/:icl_id/item/:item_id/:positive', { construction_site_id: cs.id, icl_id: ICL.id, item_id: result.id, positive: '1' });
                            });
                        }}
                    />
                );
            }
        });

        //create parent elements
        return ICLParents.map((element) => (
            <ICLParent
                key={element.id}
                title={override ? override.parentTitles[element.id] : <CustomTranslation value={element.title} />}
                iclList={ICLItemsMap[element.id]}
                icl={ICL}
                option={element}
                className={'vertical-padding-2'}
                style={{ paddingTop: 0 }}
            />
        ));
    }

    handleTranslate = async (language, rememberChoice = false) => {
        this.setState({ selectedLanguage: language, showTranslation: true }, () => {
            this.refreshICLObjects();
        });
        TranslationAction.setTranslationSession(this.state.ICL.id, { selectedLanguage: language, showTranslation: true });
        if (rememberChoice && this.state.currentUser?.id) {
            const patch = await TranslationAction.rememberLanguage(this.state.currentUser.id, language);
            this.setState(prev => ({
                currentUser: { ...prev.currentUser, ...patch }
            }));
        }
    }

    handleShowOriginal = () => {
        this.setState({ showTranslation: false }, () => {
            this.refreshICLObjects();
        });
        TranslationAction.setTranslationSession(this.state.ICL.id, { selectedLanguage: this.state.selectedLanguage, showTranslation: false });
    }

    updateTitle = (title) => {
        //clear timeout
        clearTimeout(this.titleTimeout);
        this.titleTimeout = null;

        //set timeout
        this.titleTimeout = setTimeout(async () => {

            //current language
            let user = await UserAction.getCurrentUser();
            let language = user.uiLanguage;

            //create title object
            let titleObject = {};
            titleObject[language.languageCode] = title;

            //update title
            let icl = await ICLAction.updateICL(this.state.ICL.id, {
                constructionSiteId: this.props.params.construction_site_id,
                title: titleObject
            });

            //set state
            this.setState({
                ICL: icl
            });
        }, 1000);
    };

    updateActive = async (active) => {
        
        //update active
        let icl = await ICLAction.updateICL(this.state.ICL.id, {
            constructionSiteId: this.props.params.construction_site_id,
            active: active
        });
        
        //set state
        this.setState({
            ICL: icl
        });
    };

    render = () => {
        if (this.state.data) {

            //resolve the user's saved DeepL language preference, if any, so the translate button can skip asking
            const savedLanguage = TranslationAction.resolveSavedLanguage(this.state.currentUser, this.state.availableLanguages);

            return (
                <CustomContainer
                    footer={
                        <Button
                            block
                            variant={'dark'}
                            onClick={() => {
                                toast.success(<CustomTranslation value={'KEY.Inspection Checklist saved.'} />);
                                setTimeout(() => {
                                    window.location.href = generatePath('/construction_site/:id', { id: this.state.data.constructionSite.id });
                                }, 1000);
                            }}
                        >
                            <CustomTranslation value={'KEY.Finish Inspection Checklist'} />
                        </Button>
                    }
                >
                    <ConstructionSiteWrapper
                        constructionSite={this.state.data.constructionSite}
                    >
                        <div>
                            {/* Project header */}
                            <div style={{ position: 'sticky', top: '2rem', zIndex: 9 }}>
                                <Project
                                    float
                                    data={this.state.data.constructionSite}
                                />
                            </div>

                            {/* Breadcrumb */}
                            <Breadcrumb className={'vertical-margin-2'}>
                                <Breadcrumb.Item
                                    href={generatePath('/')}
                                >
                                    <CustomTranslation value={'KEY.Home'} />
                                </Breadcrumb.Item>
                                <Breadcrumb.Item
                                    href={generatePath('/construction_site/:id', { id: this.state.data.constructionSite.id })}
                                >
                                    {this.state.data.constructionSite.name}
                                </Breadcrumb.Item>
                                <Breadcrumb.Item active>
                                    <CustomTranslation value={'KEY.Inspection Checklist'} />
                                </Breadcrumb.Item>
                            </Breadcrumb>

                            {/* Title */}
                            <div className={'vertical-margin'} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
                                <h1>
                                    <CustomTranslation value={'KEY.Inspection Checklist'} />
                                </h1>
                                <TranslationButton
                                    showTranslation={this.state.showTranslation}
                                    loading={this.state.loadingTranslations}
                                    availableLanguages={this.state.availableLanguages}
                                    savedLanguage={savedLanguage}
                                    onTranslate={this.handleTranslate}
                                    onShowOriginal={this.handleShowOriginal}
                                    onFirstInteract={this.dismissTranslationHint}
                                />
                                <TranslationHint
                                    visible={this.state.showTranslationHint}
                                    onDismiss={this.dismissTranslationHint}
                                />
                            </div>

                            <div className={'vertical-padding-2'} style={{ paddingTop: 0 }}>
                                <CustomInput
                                    iconName={'las la-quote-left'}
                                    label={<CustomTranslation value={'KEY.Description: (optional)'} />}
                                    value={this.state.title}
                                    onChangeText={(text) => {
                                        this.updateTitle(text);
                                    }}
                                />
                            </div>

                            {/* List */}
                            {this.state.ICLObjects}

                            <div className={'vertical-padding-2'} style={{ paddingTop: 0 }}>
                                <CustomCheckBox
                                    label={<CustomTranslation value={'KEY.Ready'} />}
                                    value={this.state.ICL.active}
                                    onValueChange={(value) => this.updateActive(value)}
                                />
                            </div>

                        </div>
                    </ConstructionSiteWrapper>
                </CustomContainer>
            );
        } else {
            return (
                <CustomContainer>
                    <Loader />
                </CustomContainer>
            );
        }
    }
}

//export
export default function (props) {
    const params = useParams();
    return <ICLScreen {...props} params={params} />;
}